import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

/**
 * Bridges the header's mobile menu button (rendered by FullLayout) and the
 * sidebar drawer (rendered further down by BoardWithSidebarLayout). The two
 * sit in different route layouts, so the open state has to live above both.
 *
 * Only some routes render a sidebar, so the sidebar registers itself while it
 * is mounted; the header shows its toggle only while there is something to open.
 */
interface MobileSidebarContextValue {
  /** True while a sidebar is mounted and can be opened. */
  hasSidebar: boolean;
  isOpen: boolean;
  toggle: () => void;
  close: () => void;
  /** Call on mount; the returned cleanup unregisters and closes the drawer. */
  registerSidebar: () => () => void;
}

export const MobileSidebarContext =
  createContext<MobileSidebarContextValue | null>(null);

export const MobileSidebarProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [sidebarCount, setSidebarCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const toggle = useCallback(() => {
    setIsOpen((open) => !open);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const registerSidebar = useCallback(() => {
    setSidebarCount((count) => count + 1);
    return () => {
      setSidebarCount((count) => count - 1);
      // A drawer left open on the way out would reappear open on the next
      // board visit, over whatever the user navigated to.
      setIsOpen(false);
    };
  }, []);

  const hasSidebar = sidebarCount > 0;

  const value = useMemo<MobileSidebarContextValue>(
    () => ({
      hasSidebar,
      isOpen: hasSidebar && isOpen,
      toggle,
      close,
      registerSidebar,
    }),
    [hasSidebar, isOpen, toggle, close, registerSidebar],
  );

  return (
    <MobileSidebarContext.Provider value={value}>
      {children}
    </MobileSidebarContext.Provider>
  );
};

export const useMobileSidebar = (): MobileSidebarContextValue => {
  const ctx = useContext(MobileSidebarContext);
  if (ctx === null) {
    throw new Error(
      "useMobileSidebar must be used within a MobileSidebarProvider",
    );
  }
  return ctx;
};
